/**
 * Wrapper for App to add Context providers in a way that App can consume them directly
 */
import cubejs from '@cubejs-client/core';
import { CubeProvider } from '@cubejs-client/react';
import {
  BlurPHIProvider,
  ClientAPIProvider,
  CommonUtilityWrapper,
  FlagProvider,
  HTTPError,
  provider,
  ProviderStack,
  useAuth,
  UserProfileProvider,
} from '@insidedesk/tuxedo';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { PropsWithChildren } from 'react';

import {
  keepPreviousData,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { FULL_STORY_CONFIG, USER_GUIDING_CONFIG } from './config';
import { CLIENT_API_URL } from './constants';
import {
  ChartLabelContext,
  LastUpdatedProvider,
  LoadingContextProvider,
} from './context';
import { TooltipProvider } from './contexts/TooltipContext';
import { noticeError } from './utils';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ProviderContainer({ children }: PropsWithChildren) {
  const [showChartLabels, setShowChartLabels] = React.useState(false);
  const { getAccessToken } = useAuth();
  const cubejsAPI = React.useMemo(
    () => cubejs(getAccessToken, { apiUrl: API_URL }),
    [getAccessToken],
  );

  const queryClient = React.useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (error instanceof HTTPError) {
                if (
                  error.response.status === 404 ||
                  error.response.status === 401
                ) {
                  return false;
                }
              }
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            placeholderData: keepPreviousData,
          },
        },
        queryCache: new QueryCache({
          onError: (err) => noticeError(err),
        }),
        mutationCache: new MutationCache({
          onError: (err) => noticeError(err),
        }),
      }),
    [],
  );

  const toggleChartLabels = React.useCallback(() => {
    setShowChartLabels((prev) => !prev);
  }, []);

  const chartLabelContext = React.useMemo(
    () => ({
      display: showChartLabels,
      toggleDisplay: toggleChartLabels,
    }),
    [showChartLabels, toggleChartLabels],
  );

  return (
    <ProviderStack
      providers={[
        provider(LocalizationProvider, { dateAdapter: AdapterDateFns }),
        provider(LoadingContextProvider, {}),
        provider(ClientAPIProvider, { baseURL: CLIENT_API_URL }),
        provider(UserProfileProvider, {}),
        provider(FlagProvider, {
          clientSideID: process.env.REACT_APP_LD_CLIENT_ID ?? '',
          useClientContext: true,
        }),
        provider(BlurPHIProvider, {}),
        provider(CubeProvider, { cubejsApi: cubejsAPI }),
        provider(QueryClientProvider, { client: queryClient }),
        provider(ChartLabelContext.Provider, { value: chartLabelContext }),
        provider(TooltipProvider, {}),
        provider(LastUpdatedProvider, {}),
        provider(CommonUtilityWrapper, {
          fullStoryConfigOptions: FULL_STORY_CONFIG,
          userGuidingConfig: USER_GUIDING_CONFIG,
        }),
      ]}
    >
      <ReactQueryDevtools buttonPosition='bottom-left' />
      <CssBaseline />
      {children}
    </ProviderStack>
  );
}
